<template>
  <div>
    <e-sidebar
      :title="edit ? $t('Editar Parceria') : $t('Nova Parceria')"
      :show="showSidebar"
      :fetching="fetching"
      :saving="saving"
      width="80%"
      @save="savePartnership"
      @hidden="hideSidebar"
    >
      <template #content>
        <FormulateForm
          ref="partnershipForm"
          name="form"
        >
          <b-row>
            <b-col md="4">
              <FormulateInput
                id="partnership-partner-type"
                v-model="localPartnership.partnerType"
                :type="isReadOnly ? 'label' : 'vue-select'"
                class="required"
                :label="$t('Tipo de Parceiro')"
                :options="partnerTypeOptions"
                validation="required"
              />
            </b-col>

            <b-col md="4">
              <FormulateInput
                id="partnership-partner-name"
                v-model="localPartnership.partnerName"
                :type="isReadOnly ? 'label' : 'text'"
                class="required"
                :label="$t('Nome do Parceiro')"
                validation="required"
              />
            </b-col>

            <b-col md="4">
              <FormulateInput
                id="partnership-cnpj"
                v-model="localPartnership.cnpj"
                :type="isReadOnly ? 'label' : 'text-mask'"
                :mask="['XX.XXX.XXX/XXXX-##']"
                :label="$t('CNPJ')"
                validation="optional|cpfCnpj"
              />
            </b-col>

            <b-col md="4">
              <FormulateInput
                id="partnership-estimated-participants"
                v-model="localPartnership.estimatedParticipants"
                :type="isReadOnly ? 'label' : 'number'"
                class="required"
                :label="$t('Quantidade Estimada de Participantes')"
                validation="required|min:1"
                min="1"
              />
            </b-col>

            <b-col md="4">
              <FormulateInput
                id="partnership-verification-field-name"
                v-model="localPartnership.verificationFieldName"
                :type="isReadOnly ? 'label' : 'text'"
                class="required"
                :label="$t('Nome do Campo de Verificação')"
                :instruction="
                  $t(
                    'Nome do campo que será usado para verificar se o usuário pertence à parceria (ex: Matrícula, Código do Funcionário, etc.)'
                  )
                "
                validation="required"
              />
            </b-col>

            <b-col md="4">
              <FormulateInput
                id="partnership-price-table"
                v-model="localPartnership.priceTableId"
                :type="isReadOnly ? 'label' : 'vue-select'"
                :label="$t('Tabela de Preço')"
                :options="priceTableOptions"
                :placeholder="$t('Selecione uma tabela de preço')"
              />
            </b-col>

            <b-col md="4">
              <FormulateInput
                id="partnership-key"
                v-model="localPartnership.key"
                type="label"
                :label="$t('Chave da Parceria (GUID)')"
                :placeholder="$t('Chave única da parceria')"
                :readonly="true"
                :instruction="$t('Chave única gerada automaticamente para identificar a parceria')"
              />
            </b-col>
            <b-col md="8">
              <FormulateInput
                id="partnership-url"
                v-model="localPartnership.partnershipUrl"
                :type="isReadOnly ? 'label' : 'text'"
                :label="$t('URL da Parceria')"
                :placeholder="$t('https://exemplo.com/parceria')"
              />
            </b-col>

            <!-- QR Code da URL da Parceria -->
            <b-col
              v-if="localPartnership.partnershipUrl && isValidUrl(localPartnership.partnershipUrl)"
              md="12"
              class="form-group"
            >
              <label class="formulate-input-label">
                {{ $t('QR Code da Parceria') }}
              </label>
              <div class="qr-code-container text-center p-3 border rounded">
                <div class="qr-code-wrapper">
                  <canvas
                    ref="qrCanvas"
                    class="qr-code-canvas"
                    style="max-width: 200px; height: auto"
                  />
                </div>
                <div class="mt-2">
                  <p class="text-muted small mb-2">
                    {{ $t('Escaneie este QR Code para acessar a parceria') }}
                  </p>
                  <b-button
                    size="sm"
                    variant="outline-primary"
                    @click="copyQRCodeToClipboard"
                  >
                    <feather-icon
                      icon="CopyIcon"
                      size="14"
                      class="mr-1"
                    />
                    {{ $t('Copiar QR Code') }}
                  </b-button>
                </div>
              </div>
            </b-col>
          </b-row>
        </FormulateForm>
      </template>
    </e-sidebar>
  </div>
</template>

<script>
import { BRow, BCol, BButton } from 'bootstrap-vue'
import { ESidebar } from '@/views/components'
import { v4 as uuid } from 'uuid'
import QRCode from 'qrcode'
import echopeSmallLogo from '@/assets/images/logo/echope_small.png'

const getInitialLocalPartnership = () => ({
  id: null,
  partnerType: null,
  partnerName: '',
  cnpj: '',
  estimatedParticipants: null,
  verificationFieldName: '',
  key: uuid(),
  priceTableId: null,
  partnershipUrl: '',
  active: null,
})

export default {
  components: {
    ESidebar,
    BRow,
    BCol,
    BButton,
  },

  props: {
    partnershipId: {
      type: Number,
      default: null,
    },
    partnership: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      showSidebar: false,
      fetching: false,
      edit: false,
      saving: false,
      localPartnershipId: null,
      localPartnership: getInitialLocalPartnership(),
      priceTables: [],
      isReadOnly: false,
    }
  },

  computed: {
    partnerTypeOptions() {
      return [
        { value: 'Company', label: this.$t('Empresa') },
        { value: 'Condominium', label: this.$t('Condomínio') },
      ]
    },

    priceTableOptions() {
      const options = [{ value: null, label: this.$t('Nenhuma tabela selecionada') }]

      this.priceTables.forEach(table => {
        options.push({
          value: table.id,
          label: table.name,
        })
      })

      return options
    },
  },

  watch: {
    partnership(val) {
      this.localPartnership = {
        ...val,
        partnerType: val.partnerType,
        priceTableId: val.priceTableId,
        partnershipUrl: val.partnershipUrl,
      }
    },
    partnershipId(val) {
      this.localPartnershipId = val
    },
    async localPartnershipId(val) {
      if (val) {
        try {
          this.fetching = true
          const { data: localPartnership } = await this.$http.get(
            `/api/partnerships/${this.localPartnershipId}`
          )
          this.localPartnership = {
            ...localPartnership,
            // partnerType: localPartnership.partnerType,
            priceTableId: localPartnership.priceTableId.toString(),
            // partnershipUrl: localPartnership.partnershipUrl,
          }
        } catch (error) {
          this.showGenericError({ error })
        } finally {
          this.fetching = false
        }
      } else {
        this.localPartnership = {}
      }
    },
    'localPartnership.partnershipUrl': {
      handler(newUrl) {
        if (newUrl && this.isValidUrl(newUrl)) {
          this.$nextTick(() => {
            this.generateQRCodeWithLogo(newUrl)
          })
        }
      },
      immediate: false,
    },
  },

  async mounted() {
    await this.fetchPriceTables()
  },

  methods: {
    async fetchPriceTables() {
      try {
        const { data } = await this.$http.get('/api/price-tables', {
          params: {
            pageSize: 1000,
            active: true,
          },
        })
        this.priceTables = data.results || []
      } catch (error) {
        this.showGenericError({ error })
      }
    },

    showCreate() {
      this.localPartnership = getInitialLocalPartnership()
      this.localPartnership.active = true
      this.edit = false
      this.showSidebar = true
    },

    async show({ id, item } = {}, isReadOnly = false) {
      this.isReadOnly = isReadOnly
      if (!id && !item?.id) {
        this.showCreate()
      } else {
        if (id) {
          this.localPartnershipId = id
        }
        if (item) {
          this.localPartnership = {
            ...item,
            partnerType: item.partnerType,
            priceTableId: item.priceTableId,
            partnershipUrl: item.partnershipUrl,
          }
        }
        this.edit = true
        this.showSidebar = true
      }
    },

    hideSidebar() {
      this.localPartnership = getInitialLocalPartnership()
      this.localPartnershipId = null
      this.showSidebar = false
    },

    async savePartnership() {
      this.$refs.partnershipForm.showErrors()
      if (this.$refs.partnershipForm.isValid) {
        try {
          this.saving = true

          const body = {
            id: this.localPartnership.id,
            partnerType: this.localPartnership.partnerType,
            partnerName: this.localPartnership.partnerName,
            cnpj: this.localPartnership.cnpj.replace(/\D/g, ''), // Remove formatação
            estimatedParticipants: parseInt(this.localPartnership.estimatedParticipants, 10),
            verificationFieldName: this.localPartnership.verificationFieldName,
            priceTableId: this.localPartnership.priceTableId || null,
            partnershipUrl: this.localPartnership.partnershipUrl || null,
            active: this.localPartnership.active,
            key: this.localPartnership.key,
          }

          const result = await this.$http({
            url: '/api/partnerships',
            method: this.edit ? 'PUT' : 'POST',
            data: body,
          })

          this.showSuccess({
            message: this.$t(`${this.edit ? 'Atualização' : 'Inclusão'} realizada com sucesso`),
          })
          this.showSidebar = false
          this.$emit('save', result.data)
        } catch (error) {
          this.showError({ error })
        } finally {
          this.saving = false
        }
      } else {
        this.showInvalidDataMessage()
      }
    },

    // Métodos para QR Code
    isValidUrl(url) {
      if (!url) return false
      try {
        const urlObj = new URL(url)
        return urlObj.protocol === 'http:' || urlObj.protocol === 'https:'
      } catch {
        return false
      }
    },

    async generateQRCodeWithLogo(url) {
      try {
        const canvas = this.$refs.qrCanvas
        if (!canvas) return

        const ctx = canvas.getContext('2d')

        // Gera o QR Code usando a biblioteca local
        await QRCode.toCanvas(canvas, url, {
          width: 200,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF',
          },
        })

        // Carrega e desenha o logo no centro
        const logo = new Image()
        logo.onload = () => {
          try {
            // Calcula o tamanho e posição do logo (20% do tamanho do QR Code)
            const logoSize = Math.min(canvas.width, canvas.height) * 0.2
            const logoX = (canvas.width - logoSize) / 2
            const logoY = (canvas.height - logoSize) / 2

            // Cria um fundo branco circular para o logo
            const padding = logoSize * 0.15
            const backgroundSize = logoSize + padding * 2
            const backgroundX = logoX - padding
            const backgroundY = logoY - padding
            const radius = backgroundSize / 2

            ctx.fillStyle = 'white'
            ctx.beginPath()
            ctx.arc(backgroundX + radius, backgroundY + radius, radius, 0, 2 * Math.PI)
            ctx.fill()

            // Desenha uma borda suave circular
            ctx.strokeStyle = '#e9ecef'
            ctx.lineWidth = 2
            ctx.stroke()

            // Desenha o logo
            ctx.drawImage(logo, logoX, logoY, logoSize, logoSize)
          } catch (logoError) {
            console.warn('Erro ao desenhar logo:', logoError)
            // O QR Code já está desenhado, então não há problema se o logo falhar
          }
        }

        logo.onerror = () => {
          console.warn('Erro ao carregar logo')
          // O QR Code já está desenhado, então não há problema se o logo falhar
        }

        // Carrega o logo local
        logo.src = echopeSmallLogo
      } catch (error) {
        console.warn('Erro ao gerar QR Code:', error)
      }
    },

    async copyQRCodeToClipboard() {
      try {
        const canvas = this.$refs.qrCanvas
        if (!canvas) {
          this.$toast.error(this.$t('QR Code não está disponível'))
          return
        }

        // Tenta copiar do canvas (agora sem problemas de CORS)
        canvas.toBlob(async blob => {
          try {
            if (blob && navigator.clipboard && window.ClipboardItem) {
              await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })])
              this.$toast.success(this.$t('QR Code copiado para a área de transferência!'))
            } else {
              // Fallback: download
              this.downloadCanvasAsImage(canvas)
            }
          } catch (clipboardError) {
            console.warn('Erro ao copiar para clipboard:', clipboardError)
            // Fallback: download
            this.downloadCanvasAsImage(canvas)
          }
        }, 'image/png')
      } catch (error) {
        console.warn('Erro ao copiar QR Code:', error)
        this.$toast.error(this.$t('Erro ao copiar QR Code. Tente novamente.'))
      }
    },

    downloadCanvasAsImage(canvas) {
      try {
        // Converte canvas para data URL
        const dataURL = canvas.toDataURL('image/png')

        // Cria um link temporário para download
        const link = document.createElement('a')
        link.download = 'qr-code-parceria.png'
        link.href = dataURL
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        this.$toast.info(this.$t('QR Code baixado. Use Ctrl+C para copiar após abrir a imagem.'))
      } catch (error) {
        console.warn('Erro no download:', error)
        this.$toast.error(this.$t('Erro ao baixar QR Code'))
      }
    },
  },
}
</script>

<style scoped>
.qr-code-container {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
}

.qr-code-wrapper {
  position: relative;
  display: inline-block;
}

.qr-code-image {
  border: 1px solid #e9ecef;
  border-radius: 0.25rem;
  background-color: white;
  transition: transform 0.2s ease;
}

.qr-code-image:hover {
  transform: scale(1.05);
}

.qr-code-canvas {
  border: 1px solid #e9ecef;
  border-radius: 0.25rem;
  background-color: white;
}
</style>
